import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

/**
 * Dutchie Agent Assist — Sparkle Icon
 *
 * Simplified icon component rendering the B1 sparkle cluster
 * in branded-outline style (solid fill, no background).
 *
 * Usage:
 *   <AgentAssistIcon />
 *   <AgentAssistIcon color="#7c3aed" />
 *   <AgentAssistIcon variant="outline" />
 *   <AgentAssistIcon animate />
 */

// ── Animation keyframes ──

const pulseMain = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const pulseSatA = keyframes`
  0%, 100% { opacity: var(--sat-a-opacity); transform: scale(1); }
  40% { opacity: 1; transform: scale(1.15); }
`;

const pulseSatB = keyframes`
  0%, 100% { opacity: var(--sat-b-opacity); transform: scale(1); }
  60% { opacity: 0.9; transform: scale(1.2); }
`;

// ── Styled SVG ──

const Svg = styled.svg<{ $size: number; $animate: boolean }>`
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;

  .star-main {
    animation: ${({ $animate }) => ($animate ? pulseMain : 'none')} 3s ease-in-out infinite;
  }

  .star-sat-a {
    transform-origin: 26px 9.5px;
    animation: ${({ $animate }) => ($animate ? pulseSatA : 'none')} 2.4s ease-in-out infinite 0.4s;
  }

  .star-sat-b {
    transform-origin: 7px 25.5px;
    animation: ${({ $animate }) => ($animate ? pulseSatB : 'none')} 2.8s ease-in-out infinite 0.8s;
  }
`;

// ── Types ──

interface AgentAssistIconProps {
  /** Render size in px. Default: 33 */
  size?: number;
  /** Primary color — satellites are derived from this. Default: #1a6b4a */
  color?: string;
  /** 'default' = solid filled stars. 'outline' = stroke-only main star. */
  variant?: 'default' | 'outline';
  /** When true, applies a staggered sparkle pulse animation. Default: false */
  animate?: boolean;
  className?: string;
}

// ── Satellite opacity derivation ──

const SAT_A_OPACITY = 0.55;
const SAT_B_OPACITY = 0.45;

// ── Component ──

export function AgentAssistIcon({
  size = 33,
  color = '#1a6b4a',
  variant = 'default',
  animate = false,
  className,
}: AgentAssistIconProps) {
  // ── Outline: stroke-only main star, no satellites ──

  if (variant === 'outline') {
    return (
      <Svg
        $size={size}
        $animate={animate}
        viewBox="0 0 33 33"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        style={
          {
            '--sat-a-opacity': SAT_A_OPACITY,
            '--sat-b-opacity': SAT_B_OPACITY,
          } as React.CSSProperties
        }
      >
        {/* Main 4-point star — stroke only */}
        <path
          className="star-main"
          d="M16.5 4.5L18.8 12.4L26 16L18.8 19.6L16.5 27.5L14.2 19.6L7 16L14.2 12.4Z"
          stroke={color}
          strokeWidth={1.3}
          strokeLinejoin="round"
          fill="none"
        />
        {/* Top-right satellite — solid fill, derived opacity */}
        <path
          className="star-sat-a"
          d="M26 6.5L26.7 8.6L28.6 9.5L26.7 10.4L26 12.5L25.3 10.4L23.4 9.5L25.3 8.6Z"
          fill={color}
          fillOpacity={SAT_A_OPACITY}
        />
        {/* Bottom-left satellite — solid fill, derived opacity */}
        <path
          className="star-sat-b"
          d="M7 23L7.6 24.7L9.2 25.5L7.6 26.3L7 28L6.4 26.3L4.8 25.5L6.4 24.7Z"
          fill={color}
          fillOpacity={SAT_B_OPACITY}
        />
      </Svg>
    );
  }

  // ── Default: solid filled main star + faded satellites ──

  return (
    <Svg
      $size={size}
      $animate={animate}
      viewBox="0 0 33 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={
        {
          '--sat-a-opacity': SAT_A_OPACITY,
          '--sat-b-opacity': SAT_B_OPACITY,
        } as React.CSSProperties
      }
    >
      {/* Main 4-point star */}
      <path
        className="star-main"
        d="M16.5 4.5L18.8 12.4L26 16L18.8 19.6L16.5 27.5L14.2 19.6L7 16L14.2 12.4Z"
        fill={color}
      />
      {/* Top-right satellite — 55% of main */}
      <path
        className="star-sat-a"
        d="M26 6.5L26.7 8.6L28.6 9.5L26.7 10.4L26 12.5L25.3 10.4L23.4 9.5L25.3 8.6Z"
        fill={color}
        fillOpacity={SAT_A_OPACITY}
      />
      {/* Bottom-left satellite — 45% of main */}
      <path
        className="star-sat-b"
        d="M7 23L7.6 24.7L9.2 25.5L7.6 26.3L7 28L6.4 26.3L4.8 25.5L6.4 24.7Z"
        fill={color}
        fillOpacity={SAT_B_OPACITY}
      />
    </Svg>
  );
}

export default AgentAssistIcon;
